import { useContext } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Heading, Paragraph } from '@zillow/constellation-10';

import { RMWContext } from '../../../../../../providers/RMWContext';

import { useSelector } from '../../../../../../selectors/useSelector';
import AppSelectors from '../../../../../../selectors/AppSelectors';

import propertyUtils from '../../../../../../utils/propertyUtils';
import accessUtils from '../../../../../../utils/accessUtils';
import { PropertyPhoto } from '../../../../../../components/WizardModularListing/Review/components/PropertyPhoto';
import { AccordionStateButton } from './AccordionStateButton';

import { containerStyles, pageContentStyles } from './styles';
import { singleTextLineStyles } from '../../../../../../styles';

export const TOP_OFFSET = 256;

type Props = {
  isFixedPosition?: boolean;
  isListingHeaderVisible?: boolean;
};
const AccordionStateHeader = ({ isFixedPosition = false, isListingHeaderVisible = true }: Props) => {
  const { isInMobileWebView } = useContext(RMWContext);

  const {
    name,
    street,
    city,
    state,
    zip,
    isSelfServeOnboardingInProgress,
    isWizardView,
    isScreenXlGte,
    currentProperty,
    user,
  } = useSelector((state) => {
    return {
      dateLastActivatedTimestamp: state.properties.currentProperty.dateLastActivatedTimestamp,
      name: state.properties.currentProperty.name,
      street: state.properties.currentProperty.street,
      city: state.properties.currentProperty.city,
      state: state.properties.currentProperty.state,
      zip: state.properties.currentProperty.zip,
      isSelfServeOnboardingInProgress:
        state.properties.currentProperty.selfServeOnboardingStatus.isSelfServeOnboardingInProgress,
      openLabel: state.properties.accordionState.openLabel,
      isWizardView: AppSelectors.makeIsWizardView(state),
      isScreenXlGte: AppSelectors.makeIsScreenXlGTE(state),
      currentProperty: state.properties.currentProperty,
      user: state.user,
    };
  }, shallowEqual);

  const canAccessListingQuality = accessUtils.canAccessListingQuality({
    user,
    currentProperty,
  });

  const { formattedCompleteAddress } = propertyUtils.formatAddress({
    street,
    city,
    state,
    zip,
  });

  return (
    <Box
      css={containerStyles.raw({
        isFixedPosition,
        canAccessListingQuality,
      })}
      data-testid="AccordionStateHeader"
    >
      <Box css={pageContentStyles}>
        <Box css={{ display: 'flex', flexDirection: isSelfServeOnboardingInProgress ? 'column' : 'row' }}>
          {isSelfServeOnboardingInProgress && (
            <>
              <Paragraph textStyle="body-sm-bold">Step 4 of 4</Paragraph>
              <Heading level={4}>Listing details</Heading>
            </>
          )}
          {isFixedPosition && canAccessListingQuality && (
            <>
              {isScreenXlGte && <PropertyPhoto />}
              <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
                <Heading level={3}>{name || street}</Heading>
                <Paragraph
                  css={{
                    ...singleTextLineStyles,
                  }}
                >
                  {formattedCompleteAddress}
                </Paragraph>
              </Box>
            </>
          )}
          {!canAccessListingQuality && <Heading level={4}>{isFixedPosition ? name || street : name}</Heading>}
        </Box>
        <Box css={{ display: 'flex', marginRight: '200' }}>
          <AccordionStateButton isVisible={!canAccessListingQuality || !isListingHeaderVisible} />
        </Box>
      </Box>
    </Box>
  );
};

export default AccordionStateHeader;
